/**
 * SmartLink capacity — dash data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/smartlink-capacity` surface (feature #113, OPUS-113). Mirrors
 * the contract declared in `apps/api/src/routes/smartlink-capacity.ts`.
 *
 * Zero external deps beyond the root `apiClient` — no React, no TanStack
 * — so the module is reusable from pure-logic tests and any future
 * server-side smoke harness.
 *
 * Endpoints (see backend file for the authoritative spec):
 *   PUT    /                 upsert a cap row
 *   POST   /increment        bump used; learn over_cap / remaining
 *   POST   /filter           filter a list of offer_ids by remaining cap
 *   GET    /:offer_id        list cap rows for an offer (tenant-scoped)
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Canonical domain types — 1:1 with the backend enum. Kept frozen and
// exported so UI + tests share the same constant surface.
// ---------------------------------------------------------------------------

export const CAP_TYPES = ["daily", "monthly", "lifetime"] as const;
export type CapType = (typeof CAP_TYPES)[number];

export const CAP_TYPE_LABELS: Record<CapType, string> = {
	daily: "Daily",
	monthly: "Monthly",
	lifetime: "Lifetime",
};

export const CAP_TYPE_HINTS: Record<CapType, string> = {
	daily: "Resets every day — pair with a YYYY-MM-DD window key.",
	monthly: "Resets every month — pair with a YYYY-MM window key.",
	lifetime: "One running total — use 'lifetime' as the window key.",
};

// Kept in sync with the backend constants. Any drift here is caught by
// the validator in `capEditorSchema` below.
export const MAX_ID_LENGTH = 128;
export const MAX_WINDOW_KEY_LENGTH = 64;
export const MAX_CAP_LIMIT = 2_000_000_000;
export const MAX_INCREMENT_QTY = 1_000_000;
export const MAX_FILTER_BATCH = 1000;

// ---------------------------------------------------------------------------
// API row / response shapes
// ---------------------------------------------------------------------------

export interface OfferCapView {
	offer_id: string;
	cap_type: CapType;
	cap_limit: number;
	used: number;
	window_key: string;
	created_at: number;
	updated_at: number;
}

export interface ListCapsResponse {
	offer_id: string;
	caps: OfferCapView[];
}

export interface IncrementResponse {
	offer_id: string;
	cap_type: CapType;
	window_key: string;
	cap_limit: number;
	used: number;
	remaining: number;
	over_cap: boolean;
}

export interface FilterResponse {
	cap_type: CapType;
	window_key: string;
	requested: number;
	offer_ids: string[];
}

// ---------------------------------------------------------------------------
// Pure helpers for the UI layer
// ---------------------------------------------------------------------------

export function isCapType(v: unknown): v is CapType {
	return typeof v === "string" && (CAP_TYPES as readonly string[]).includes(v);
}

/**
 * Remaining capacity for a row, clamped to zero. Mirrors the backend
 * helper so the UI shows the same number the tracker sees.
 */
export function remainingFor(row: {
	cap_limit: number;
	used: number;
}): number {
	const delta = row.cap_limit - row.used;
	return delta > 0 ? delta : 0;
}

/**
 * "filled percent" for the progress bar — returns a number 0..100 with
 * two decimals of precision. A cap_limit of 0 is treated as fully
 * consumed so the bar snaps to 100% (rose) to highlight the hard zero.
 */
export function filledPct(row: {
	cap_limit: number;
	used: number;
}): number {
	if (row.cap_limit <= 0) return 100;
	const raw = (row.used / row.cap_limit) * 100;
	if (!Number.isFinite(raw)) return 100;
	if (raw < 0) return 0;
	if (raw > 100) return 100;
	return Math.round(raw * 100) / 100;
}

export function isOverCap(row: {
	cap_limit: number;
	used: number;
}): boolean {
	return row.used >= row.cap_limit;
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled .safeParse surface (zero dep on zod) so the
// page can render inline errors and the test suite can assert the exact
// shape without pulling a validation lib into this lane.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface CapEditorInput {
	offerId: string;
	capType: CapType | string;
	capLimit: number | string;
	windowKey: string;
}

export interface CapEditorParsed {
	offer_id: string;
	cap_type: CapType;
	cap_limit: number;
	window_key: string;
}

const WINDOW_KEY_RE = /^[\w\-:.]+$/;

export const capEditorSchema = {
	safeParse(input: CapEditorInput): ParseResult<CapEditorParsed> {
		const errors: Record<string, string> = {};

		const offerId =
			typeof input.offerId === "string" ? input.offerId.trim() : "";
		if (offerId.length === 0) {
			errors.offerId = "Offer ID is required.";
		} else if (offerId.length > MAX_ID_LENGTH) {
			errors.offerId = `Offer ID must be ${MAX_ID_LENGTH} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(offerId)) {
			errors.offerId = "Offer ID contains invalid control characters.";
		}

		if (!isCapType(input.capType)) {
			errors.capType = "Cap type must be daily, monthly, or lifetime.";
		}

		const limitNum =
			typeof input.capLimit === "number"
				? input.capLimit
				: Number.parseInt(String(input.capLimit).trim(), 10);
		if (!Number.isFinite(limitNum) || !Number.isInteger(limitNum)) {
			errors.capLimit = "Limit must be a whole number.";
		} else if (limitNum <= 0) {
			errors.capLimit = "Limit must be greater than zero.";
		} else if (limitNum > MAX_CAP_LIMIT) {
			errors.capLimit = `Limit must be ${MAX_CAP_LIMIT.toLocaleString()} or fewer.`;
		}

		const windowKey =
			typeof input.windowKey === "string" ? input.windowKey.trim() : "";
		if (windowKey.length === 0) {
			errors.windowKey = "Window key is required.";
		} else if (windowKey.length > MAX_WINDOW_KEY_LENGTH) {
			errors.windowKey = `Window key must be ${MAX_WINDOW_KEY_LENGTH} chars or fewer.`;
		} else if (!WINDOW_KEY_RE.test(windowKey)) {
			errors.windowKey =
				"Window key may only contain letters, digits, '-', ':', '.', '_'.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				offer_id: offerId,
				cap_type: input.capType as CapType,
				cap_limit: limitNum,
				window_key: windowKey,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Parse a textarea of offer IDs → deduplicated, trimmed array. The
// filter tester feeds this directly into POST /filter.
// ---------------------------------------------------------------------------

export function parseOfferIdList(raw: string): {
	ids: string[];
	dropped: number;
} {
	const seen = new Set<string>();
	const ids: string[] = [];
	let dropped = 0;
	for (const part of raw.split(/[,\s]+/g)) {
		const t = part.trim();
		if (t.length === 0) continue;
		if (t.length > MAX_ID_LENGTH) {
			dropped += 1;
			continue;
		}
		if (seen.has(t)) continue;
		seen.add(t);
		ids.push(t);
	}
	return { ids, dropped };
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/smartlink-capacity";

export interface UpsertCapArgs {
	offer_id: string;
	cap_type: CapType;
	cap_limit: number;
	window_key: string;
	used?: number;
}

export async function upsertCap(
	args: UpsertCapArgs,
): Promise<OfferCapView> {
	return apiClient.put<OfferCapView>(BASE, args);
}

export async function listCapsForOffer(
	offerId: string,
): Promise<OfferCapView[]> {
	try {
		const res = await apiClient.get<ListCapsResponse>(
			`${BASE}/${encodeURIComponent(offerId)}`,
		);
		return res.caps ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface IncrementCapArgs {
	offer_id: string;
	cap_type: CapType;
	window_key: string;
	qty?: number;
}

export async function incrementCap(
	args: IncrementCapArgs,
): Promise<IncrementResponse> {
	return apiClient.post<IncrementResponse>(`${BASE}/increment`, args);
}

export interface FilterCapsArgs {
	offer_ids: string[];
	cap_type: CapType;
	window_key: string;
}

export async function filterCappedOffers(
	args: FilterCapsArgs,
): Promise<FilterResponse> {
	return apiClient.post<FilterResponse>(`${BASE}/filter`, args);
}

// ---------------------------------------------------------------------------
// Query keys — consumed by SmartlinkCapacityPage.
// ---------------------------------------------------------------------------

export const smartlinkCapacityQueryKeys = {
	all: ["smartlink-capacity"] as const,
	byOffer: (offerId: string) =>
		[...smartlinkCapacityQueryKeys.all, "offer", offerId] as const,
};
